import { useTranslation } from "react-i18next";
import { zodResolver } from "@hookform/resolvers/zod";
import { SubmitHandler, useForm } from "react-hook-form";
import { useEffect } from "react";
import { useUpdateWoodboxesPrice } from "@/api/woodboxPrice.api";
import { UpdateGlobalParameterDto } from "@/types/api/api-types";
import {
  UpdateWoodboxesPriceFormData,
  UpdateWoodboxesPriceSchema,
} from "@/types/forms/woodbox.form";
import StyledSpinner from "@/components/styled/StyledSpinner";
import { Modal } from "@/components/ui/customs/modal";
import { Button } from "@/components/ui/button";
import { ControlledUnifiedInput } from "@/components/ui/customs/controlled-unified-input";

type OptionsWoodboxModalProps = {
  closeModal: () => void;
  woodBoxesPrice: UpdateGlobalParameterDto["woodBoxesPrice"];
  title: string;
};

export const OptionsWoodboxModal = ({
  closeModal,
  woodBoxesPrice,
  title,
}: OptionsWoodboxModalProps) => {
  const { t } = useTranslation("boParameters");
  const { mutateAsync: updateWoodboxesPrice, isPending } = useUpdateWoodboxesPrice();

  const woodboxesPriceForm = useForm<UpdateWoodboxesPriceFormData>({
    resolver: zodResolver(UpdateWoodboxesPriceSchema),
    mode: "onSubmit",
    reValidateMode: "onChange",
    defaultValues: woodBoxesPrice ?? [],
  });

  const onSubmit: SubmitHandler<UpdateWoodboxesPriceFormData> = async (data) => {
    const ret = await updateWoodboxesPrice(data);
    if (ret?.status === 200) {
      closeModal();
    }
  };

  useEffect(() => {
    if (woodBoxesPrice) {
      woodboxesPriceForm.reset(
        woodBoxesPrice.map((item) => ({
          id: item.id,
          bottleVolume: item.bottleVolume,
          bottleQuantity: item.bottleQuantity,
          price: item.price,
          region: item.region,
          globalParametersId: item.globalParametersId ?? undefined,
          companyId: item.companyId ?? undefined,
        })),
      );
    }
  }, [woodBoxesPrice, woodboxesPriceForm]);

  const firstItem = woodBoxesPrice?.[0];

  return (
    <Modal
      open
      onClose={closeModal}
      title={title}
      size="sm"
      contentClassName="max-w-[480px]"
      footer={
        <Button type="submit" form="woodboxesPriceForm" disabled={isPending} className="w-full">
          {isPending ? <StyledSpinner /> : t("woodboxModal.submit")}
        </Button>
      }
    >
      <form
        id="woodboxesPriceForm"
        onSubmit={woodboxesPriceForm.handleSubmit(onSubmit)}
        className="flex flex-col gap-4 pt-2"
      >
        {firstItem && (
          <p className="text-xs font-medium text-gray-500">
            {t("woodboxModal.groupLabel", {
              bottleVolume: firstItem.bottleVolume,
              region: firstItem.region,
            })}
          </p>
        )}
        {woodBoxesPrice?.map((item, index) => (
          <div key={item.id} className="flex flex-row items-center gap-2">
            <p className="w-1/2 text-base font-semibold text-gray-900">
              {t("woodboxModal.bottleCount", { count: item.bottleQuantity })}
            </p>
            <ControlledUnifiedInput
              control={woodboxesPriceForm.control}
              name={`${index}.price`}
              inputType="integer"
            />
          </div>
        ))}
      </form>
    </Modal>
  );
};
